
import WindowUtils from '@/utils/window-utils';
import Logger from '@/utils/logger.utils';
import { loginService } from '@/services/login.service';
import { commonService, encodeService, oidcService, oktaService, siteConfigService, storageService, userService, StringUtils } from '@ol/ol-ui-core';

class OidcCallbackProcessor {
    private WHERE_TOGO_URL = 'redirect.to.url';
    private CC_ORG_LOGGED_IN_KEY = 'ccOrgLoggedIn';

    /**
     * =========================================================================
     * LOGIN FLOW METHODS
     * =========================================================================
     */
    /**
     * Checks the current URL and user status to determine the next action:
     * 1. Handle OIDC Callback and redirect to home.
     * 2. Redirect to Okta for login.
     * 3. Allow application to proceed (user is already logged in).
     * * @param plId The Private Label ID.
     * @returns A promise that resolves to true if execution should STOP (a redirect occurred),
     * or false if the main application should proceed.
     */
    public async checkAndHandleOidcLoginCallbackFlow(plId: number, ccOrg: boolean): Promise<boolean> {
        if (await loginService.isLoggedIn()) {
            const ccOrgLoggedIn = localStorage.getItem(this.CC_ORG_LOGGED_IN_KEY);
            if (ccOrgLoggedIn === null) {
                await this.redirectToCcOrgLoginCheck(plId);
                return true;
            }

            if (ccOrgLoggedIn === 'true') {
                const shouldStop = await this.handleOidcLoginCallbackIfPresent(plId, ccOrg);
                if (shouldStop) return true;
                return this.handleInitialLoginIfRequired(plId, ccOrg);
            }
        }
        return false;
    }

    private async redirectToCcOrgLoginCheck(plId: number) {
        const oktaLoginAuthCheckUrl = await siteConfigService.getApiEndpoint(plId, 'auth.okta.oktaLoginAuthCheckUrl');
        let oktaRedirectAfterLogin = await siteConfigService.getApiEndpoint(plId, 'auth.okta.oktaRedirectAfterLogin');
        const redirectPayload = encodeService.encodeJSON({
            plId,
            redirectUri: window.location.href,
        });
        oktaRedirectAfterLogin = StringUtils.setOrAppendParamValue(oktaRedirectAfterLogin, 'p', encodeURIComponent(redirectPayload));
        const targetUrl =
            `${oktaLoginAuthCheckUrl}?plId=${plId}&redirectUri=${encodeURIComponent(oktaRedirectAfterLogin)}`;

        WindowUtils.setLocationHref(targetUrl);
    }


    private async handleOidcLoginCallbackIfPresent(plId: number, ccOrg: boolean): Promise<boolean> {
        const isOidcCallbackPath = window.location.pathname.includes('/login/oidc/callback');
        const hasAuthParams = window.location.search.includes('code=') && window.location.search.includes('state=');
        if (isOidcCallbackPath && hasAuthParams) {
            const whereToGo = sessionStorage.getItem(this.WHERE_TOGO_URL);
            let finalRedirectUrl = `/oliq/?plId=${plId}#/page/HOME_PAGE`;
            try {
                await oidcService.signInCallback(plId, ccOrg);
                const tokens = await oidcService.getTokens(plId, ccOrg);
                await storageService.setItem('okta-token-storage', JSON.stringify(tokens.oidcTokens), false);
                if (tokens.oidcTokens?.accessToken?.claims?.cci_master_id) {
                    commonService.updateCommonPropertiesUserInfo(plId, true);
                }
                if (whereToGo) {
                    finalRedirectUrl = whereToGo;
                    sessionStorage.removeItem(this.WHERE_TOGO_URL);
                }
                WindowUtils.setLocationHref(finalRedirectUrl);
                return true;
            } catch (error) {
                Logger.error(`OIDC Callback Failed in OidcCallbackProcessor: ${error}`);
                sessionStorage.removeItem(this.WHERE_TOGO_URL);
                WindowUtils.setLocationHref(finalRedirectUrl);
                return true;
            }
        }
        return false;
    }

    public async isOktaUserLogIn(plId: number): Promise<boolean> {
        const ssoToken: string = await userService.getSsoToken(plId);
        const migrationStatus = await oktaService.getOktaUserMigrationStatus(plId, ssoToken, null);
        // We can add an MFA enabled condition so it doesn't affect the current Okta login.
        return migrationStatus && migrationStatus.oidcStatus && migrationStatus.oidcStatus.migrated;
    }


    private async handleInitialLoginIfRequired(plId: number, ccOrg: boolean): Promise<boolean> {
        if (await this.isOktaUserLogIn(plId)) {
            const user = await oidcService.getUser(plId, ccOrg);
            if (!user) {
                sessionStorage.setItem(this.WHERE_TOGO_URL, window.location.href);
                Logger.info('User not found in bOrg. Initiating sign-in redirect to Okta.');
                await oidcService.signIn(plId, null, ccOrg);
            }
        }
        return false;
    }
}

const oidcCallbackProcessor = new OidcCallbackProcessor();
export default oidcCallbackProcessor;
